// src/components/modals/BugReportModal.tsx
// Simple bug report modal - allows users to freely describe bugs
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.tsx";
import "./BugReportModal.css";

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  // Form state
  const [bugTitle, setBugTitle] = useState<string>("");
  const [pageLocation, setPageLocation] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [stepsToReproduce, setStepsToReproduce] = useState<string>("");
  const [expectedBehavior, setExpectedBehavior] = useState<string>("");
  const [reporterName, setReporterName] = useState<string>(user?.username || "");
  const [reporterEmail, setReporterEmail] = useState<string>(user?.email || "");
  const [screenshots, setScreenshots] = useState<File[]>([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setBugTitle("");
      setPageLocation(window.location.pathname);
      setDescription("");
      setStepsToReproduce("");
      setExpectedBehavior("");
      setReporterName(user?.username || "");
      setReporterEmail(user?.email || "");
      setScreenshots([]);
      setSubmitError("");
      setSubmitSuccess(false);
    }
  }, [isOpen, user]);

  // Handle screenshot selection
  const handleScreenshotSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      // Check file type
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        errors.push(`${file.name}: Only images allowed`);
        return;
      }
      // Check file size (500KB max)
      if (file.size > 500 * 1024) {
        errors.push(`${file.name}: Must be under 500KB`);
        return;
      }
      // Check total count
      if (screenshots.length + newFiles.length >= 3) {
        errors.push(`Maximum 3 screenshots allowed`);
        return;
      }
      newFiles.push(file);
    });

    if (errors.length > 0) {
      setSubmitError(errors.join(". "));
      setTimeout(() => setSubmitError(""), 5000);
    }

    setScreenshots([...screenshots, ...newFiles]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove screenshot
  const removeScreenshot = (index: number) => {
    setScreenshots(screenshots.filter((_, i) => i !== index));
  };

  // Submit bug report
  const handleSubmit = async () => {
    // Validate
    if (!bugTitle.trim() || bugTitle.trim().length < 5) {
      setSubmitError("Please provide a brief title for the bug (at least 5 characters)");
      return;
    }
    if (!description.trim() || description.trim().length < 10) {
      setSubmitError("Please describe the bug in more detail (at least 10 characters)");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const formData = new FormData();

      // Bug info - map to existing API fields
      formData.append("test_id", "USER-BUG");
      formData.append("category", "User Bug Report");
      formData.append("page_section", pageLocation || "Unknown");
      formData.append("item_type", "Bug Report");
      formData.append("description", bugTitle);
      formData.append("expected_result", expectedBehavior || "Not specified");

      // Bug details
      formData.append("actual_result", description);
      formData.append("steps_to_reproduce", stepsToReproduce);
      formData.append("comments", `Page: ${pageLocation}\n\n${stepsToReproduce ? `Steps to reproduce:\n${stepsToReproduce}\n\n` : ''}${expectedBehavior ? `Expected behavior:\n${expectedBehavior}` : ''}`);

      // Reporter info
      if (user?.userId) {
        formData.append("user_id", user.userId);
      }
      formData.append("reporter_name", reporterName);
      formData.append("reporter_email", reporterEmail);

      // Browser info
      formData.append("browser_info", navigator.userAgent);
      formData.append("screen_resolution", `${window.screen.width}x${window.screen.height}`);

      // Screenshots
      screenshots.forEach(file => {
        formData.append("screenshots", file);
      });

      const apiUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiUrl}/api/bug-reports`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details?.join(". ") || "Failed to submit bug report");
      }

      setSubmitSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error: any) {
      console.error("Bug report submission error:", error);
      setSubmitError(error.message || "Failed to submit bug report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Success message
  if (submitSuccess) {
    return (
      <div className="bug-report-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="bug-report-modal bug-report-success">
          <div className="bug-report-success-icon">&#10003;</div>
          <h2>Bug Report Submitted!</h2>
          <p>Thank you for helping us improve Expanded Chess.</p>
          <p>Your feedback is valuable to us.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bug-report-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bug-report-modal">
        {/* Header */}
        <div className="bug-report-header">
          <h2>Report a Bug</h2>
          <button className="bug-report-close" onClick={onClose} title="Close">
            &times;
          </button>
        </div>

        {/* Error message */}
        {submitError && (
          <div className="bug-report-error">
            {submitError}
          </div>
        )}

        {/* Bug Report Form */}
        <div className="bug-report-form">
          <div className="form-group">
            <label htmlFor="bugTitle">Bug Title *</label>
            <input
              type="text"
              id="bugTitle"
              value={bugTitle}
              onChange={(e) => setBugTitle(e.target.value)}
              placeholder="Brief summary of the issue..."
              maxLength={200}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="pageLocation">Page/Location</label>
            <input
              type="text"
              id="pageLocation"
              value={pageLocation}
              onChange={(e) => setPageLocation(e.target.value)}
              placeholder="Where did this happen? (e.g., Play Online, Multiplayer)"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">What happened? *</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the bug in detail. What did you see or experience?"
              rows={4}
              maxLength={2000}
              required
            />
            <span className="char-count">{description.length}/2000</span>
          </div>

          <div className="form-group">
            <label htmlFor="steps">Steps to reproduce (optional)</label>
            <textarea
              id="steps"
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. Observe..."
              rows={3}
              maxLength={2000}
            />
          </div>

          <div className="form-group">
            <label htmlFor="expected">What should have happened? (optional)</label>
            <textarea
              id="expected"
              value={expectedBehavior}
              onChange={(e) => setExpectedBehavior(e.target.value)}
              placeholder="What did you expect to happen instead?"
              rows={2}
              maxLength={1000}
            />
          </div>

          {/* Screenshots */}
          <div className="form-group">
            <label>Screenshots (optional, max 3, 500KB each)</label>
            <div className="screenshot-section">
              {screenshots.length < 3 && (
                <button
                  type="button"
                  className="screenshot-add-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  + Add Screenshot
                </button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleScreenshotSelect}
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                style={{ display: "none" }}
              />

              {screenshots.length > 0 && (
                <div className="screenshot-preview-list">
                  {screenshots.map((file, index) => (
                    <div key={index} className="screenshot-preview">
                      <img src={URL.createObjectURL(file)} alt={`Screenshot ${index + 1}`} />
                      <button
                        type="button"
                        className="screenshot-remove"
                        onClick={() => removeScreenshot(index)}
                        title="Remove"
                      >
                        &times;
                      </button>
                      <span className="screenshot-name">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Reporter info */}
          <div className="form-row">
            <div className="form-group half">
              <label htmlFor="reporterName">Your name (optional)</label>
              <input
                type="text"
                id="reporterName"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
                placeholder="Name"
                maxLength={100}
              />
            </div>
            <div className="form-group half">
              <label htmlFor="reporterEmail">Your email (optional)</label>
              <input
                type="email"
                id="reporterEmail"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                placeholder="email@example.com"
                maxLength={255}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bug-report-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !bugTitle.trim() || !description.trim()}
          >
            {isSubmitting ? "Submitting..." : "Submit Bug Report"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
